import { AnchorHTMLAttributes, ButtonHTMLAttributes, forwardRef, ReactNode, useState } from "react"
import { type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

import { buttonSpinnerVariants, buttonVariants } from "./buttonVariants.tsx"
import Spinner from "./Spinner.tsx"

export interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  children?: ReactNode
  loading?: boolean
}

export interface IButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof buttonVariants> {
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  ({ className, variant, size, children, loading, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={"button"}
        className={cn(buttonVariants({ variant, size, className }), { "opacity-50": disabled })} // do not want to have opacity-50 when loading: true and disabled: false
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className="absolute inset-0 z-10 -m-[1px] flex items-center justify-center bg-neutral-mainBg">
            <Spinner large className={buttonSpinnerVariants({ size })} />
          </div>
        )}
        {children}
      </button>
    )
  },
)

Button.displayName = "Button"

export const ButtonLink = forwardRef<HTMLAnchorElement, IButtonLinkProps>(
  ({ className, variant, size, children, ...props }, ref) => {
    return (
      <a ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props}>
        {children}
      </a>
    )
  },
)

export const ButtonAsync = forwardRef<HTMLButtonElement, IButtonProps & { onClick: () => Promise<unknown> }>(
  ({ onClick, children, ...props }, ref) => {
    const [loading, setLoading] = useState(false)

    const onClickAction = () => {
      setLoading(true)
      onClick().finally(() => setLoading(false))
    }

    return (
      <Button ref={ref} onClick={onClickAction} {...props} loading={loading || props.loading} children={children} />
    )
  },
)
